import {
  Boxes,
  FileSpreadsheet,
  Building2,
  LayoutDashboard,
  ListTree,
  LogOut,
  Menu,
  QrCode,
  Recycle,
  Search,
  ShieldCheck,
  UserCircle,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import type { Permission } from '../../api/types';
import { cn } from '../../lib/cn';
import { ROLE_LABELS } from '../../lib/format';

type NavItem = { to: string; label: string; icon: ComponentType<{ className?: string }>; permission?: Permission; end?: boolean };

const NAV: Array<{ title?: string; items: NavItem[] }> = [
  {
    items: [
      { to: '/', label: 'Übersicht', icon: LayoutDashboard, end: true },
      { to: '/assets', label: 'Assets', icon: Boxes },
      { to: '/scan', label: 'Scannen', icon: QrCode },
      { to: '/disposals', label: 'Verschrottungen', icon: Recycle },
      { to: '/reconciliations', label: 'Inventarabgleich', icon: FileSpreadsheet },
    ],
  },
  {
    title: 'Stammdaten',
    items: [
      { to: '/employees', label: 'Mitarbeiter', icon: Users },
      { to: '/locations', label: 'Standorte', icon: Building2 },
      { to: '/asset-types', label: 'Asset-Typen', icon: ListTree },
    ],
  },
  {
    title: 'Verwaltung',
    items: [{ to: '/users', label: 'Benutzer', icon: ShieldCheck, permission: 'user:manage' }],
  },
];

function QuickSearch({ onDone }: { onDone?: () => void }) {
  const [term, setTerm] = useState('');
  const navigate = useNavigate();
  return (
    <form
      role="search"
      className="relative"
      onSubmit={(e) => {
        e.preventDefault();
        const t = term.trim();
        if (!t) return;
        navigate(`/assets?q=${encodeURIComponent(t)}`);
        setTerm('');
        onDone?.();
      }}
    >
      <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-slate-400" />
      <input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Suchen: IT-000123, Seriennr. …"
        aria-label="Assets suchen"
        className="h-8 w-full rounded-md border border-slate-300 bg-white pr-2 pl-8 text-sm placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none"
      />
    </form>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { me, can, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-4">
        <div className="flex size-7 items-center justify-center rounded-md bg-brand-600 text-xs font-bold text-white">IT</div>
        <span className="font-semibold text-slate-900">IT-Assets</span>
      </div>
      <div className="p-3">
        <QuickSearch onDone={onNavigate} />
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4">
        {NAV.map((group, gi) => {
          const items = group.items.filter((i) => !i.permission || can(i.permission));
          if (items.length === 0) return null;
          return (
            <div key={gi}>
              {group.title && <div className="mb-1 px-2 text-xs font-semibold tracking-wide text-slate-400 uppercase">{group.title}</div>}
              <ul className="space-y-0.5">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium',
                          isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                        )
                      }
                    >
                      <item.icon className="size-4" />
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <NavLink
          to="/account"
          onClick={onNavigate}
          className={({ isActive }) => cn('flex items-center gap-2 rounded-md px-2 py-1.5 text-sm', isActive ? 'bg-brand-50' : 'hover:bg-slate-100')}
        >
          <UserCircle className="size-5 text-slate-400" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-slate-800">{me.display_name}</span>
            <span className="block truncate text-xs text-slate-500">{ROLE_LABELS[me.role] ?? me.role}</span>
          </span>
        </NavLink>
        <button type="button" onClick={logout} className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100">
          <LogOut className="size-4" /> Abmelden
        </button>
      </div>
    </div>
  );
}

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen">
      <aside className="no-print fixed inset-y-0 left-0 z-30 hidden w-60 border-r border-slate-200 bg-white lg:block">
        <Sidebar />
      </aside>

      <header className="no-print sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:hidden">
        <button type="button" onClick={() => setMobileOpen(true)} className="rounded p-1.5 text-slate-600 hover:bg-slate-100" aria-label="Menü öffnen">
          <Menu className="size-5" />
        </button>
        <span className="font-semibold">IT-Assets</span>
      </header>

      {mobileOpen && (
        <div className="no-print fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">
            <button type="button" onClick={() => setMobileOpen(false)} className="absolute top-3.5 right-3 rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Menü schließen">
              <X className="size-5" />
            </button>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="lg:pl-60">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
